import React, { useState, useEffect, useCallback, useRef } from 'react';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Link } from '@mui/material';

import { apiFetch } from '../utils/api';

function RecipesGallery({ onlyPrincipal, searchQuery }) {

    const limit = 15;
    const [recipes, setRecipes] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMoreResults, setHasMoreResults] = useState(true);
    // Ruta de la imagen por defecto
    const defaultImage = '/static/images/recipes/default.jpg';

    const prevSearchQuery = useRef(searchQuery);

    const loadRecipes = useCallback(async (page, searchQuery, reset = false) => {
        setLoading(true);

        let url = onlyPrincipal ? `/api/recipes?page=${page}&limit=${limit}` : `/api/recipes/all?page=${page}&limit=${limit}`;

        if (searchQuery) {
            url += `&query=${encodeURIComponent(searchQuery)}`;
        }

        try {
            const response = await apiFetch(url);
            const data = await response.json();
            if (data) {
                setHasMoreResults(data.hasMoreResults);

                if (reset) {
                    setRecipes(data.results);
                } else {
                    setRecipes((prevImages) => [...prevImages, ...data.results]);
                }
            }
        } catch (error) {
            console.error('Error loading images:', error);
        } finally {
            setLoading(false);
        }
    }, [onlyPrincipal, limit]);

    useEffect(() => {
        // Detectar si la query ha cambiado y resetear la página
        if (searchQuery !== prevSearchQuery.current) {
            setPage(1);
            prevSearchQuery.current = searchQuery;
        }
        loadRecipes(page, searchQuery, page === 1);
    }, [page, searchQuery, loadRecipes]);

    const handleLoadMore = () => {
        setPage((prevPage) => prevPage + 1);
    };

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('xs'));
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isMd = useMediaQuery(theme.breakpoints.down('md'));

    let cols = 3;
    if (isXs) {
        cols = 1;
    } else if (isSm) {
        cols = 2;
    } else if (isMd) {
        cols = 3;
    }

    return (
        <>
            {recipes.length === 0 && !loading && (
                <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                    No se encontraron recetas para tu búsqueda.
                </Typography>
            )}

            <ImageList cols={cols}>
                {recipes.map((recipe) => (
                    <ImageListItem key={recipe._id} component={Link} href={`/recipes/${recipe._id}`} style={{ textDecoration: 'none' }}>
                        <img
                            srcSet={`${recipe.image}`}
                            src={`${recipe.image}`}
                            alt={recipe.title}
                            loading="lazy"
                            onError={(e) => {
                                if (e.target.src !== defaultImage) {
                                    e.target.onerror = null; // Evita bucles
                                    e.target.src = defaultImage;
                                    e.target.removeAttribute('srcSet');
                                }
                            }}
                        />
                        <ImageListItemBar
                            title={recipe.title}
                            subtitle={recipe.author.username}
                            actionIcon={
                                <IconButton
                                    sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                    aria-label={`info about ${recipe.title}`}
                                >
                                    <InfoIcon />
                                </IconButton>
                            }
                        />
                    </ImageListItem>
                ))}
            </ImageList>
            {hasMoreResults && !loading && (
                <Button variant="contained" onClick={handleLoadMore} sx={{ display: 'block', margin: '20px auto' }}>
                    Cargar más
                </Button>
            )}
            {hasMoreResults && loading && (
                <div style={{ textAlign: 'center', margin: '20px auto' }}>
                    <CircularProgress />
                </div>
            )}
        </>
    );
}

export default RecipesGallery;
